import { useEffect, useRef, useState } from "react";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import type { components } from "@dolfin/api-admin";
import { useDolfinClient } from "@/components/DolfinProvider";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

type BankDetails = components["schemas"]["BankDetails"];
type CountryProfile = components["schemas"]["CountryBankProfile"];
type CountrySummary = components["schemas"]["CountryBankProfileSummary"];
type CurrencyResponse = components["schemas"]["CurrencyResponse"];

/** Editable bank details as plain (non-null) strings, suited to controlled inputs. */
export interface BankDetailsValue {
  accountHolderName: string;
  accountNumber: string;
  sortCode: string;
  routingNumber: string;
  iban: string;
  bicSwift: string;
  country: string;
  currency: string;
}

export const emptyBankDetails: BankDetailsValue = {
  accountHolderName: "",
  accountNumber: "",
  sortCode: "",
  routingNumber: "",
  iban: "",
  bicSwift: "",
  country: "",
  currency: "",
};

export function bankDetailsToValue(
  details: BankDetails | null | undefined,
): BankDetailsValue {
  return {
    accountHolderName: details?.accountHolderName ?? "",
    accountNumber: details?.accountNumber ?? "",
    sortCode: details?.sortCode ?? "",
    routingNumber: details?.routingNumber ?? "",
    iban: details?.iban ?? "",
    bicSwift: details?.bicSwift ?? "",
    country: details?.country ?? "",
    currency: details?.currency ?? "",
  };
}

function isEmptyBankDetails(value: BankDetailsValue): boolean {
  return Object.values(value).every((v) => !v.trim());
}

/** Null when every field is blank, so we don't send an empty bank-details object. */
export function bankDetailsToRequest(value: BankDetailsValue): BankDetails | null {
  if (isEmptyBankDetails(value)) return null;
  return {
    accountHolderName: value.accountHolderName.trim() || null,
    accountNumber: value.accountNumber.trim() || null,
    sortCode: value.sortCode.replace(/\D/g, "") || null,
    routingNumber: value.routingNumber.replace(/\D/g, "") || null,
    iban: value.iban.trim().toUpperCase() || null,
    bicSwift: value.bicSwift.trim().toUpperCase() || null,
    country: value.country.trim().toUpperCase() || null,
    currency: value.currency.trim().toUpperCase() || null,
  };
}

export function hasBankDetails(details: BankDetails | null | undefined): boolean {
  if (!details) return false;
  return Object.values(details).some((v) => Boolean(v));
}

const bankProfileKeys = [
  "accountHolderName",
  "accountNumber",
  "sortCode",
  "routingNumber",
  "iban",
  "bicSwift",
] as const;

function isBankFieldVisible(
  profile: CountryProfile | null,
  key: (typeof bankProfileKeys)[number],
): boolean {
  if (!profile) return true;
  const spec = profile[key];
  if (!spec) return key === "accountHolderName";
  return spec.requirement !== "Hidden";
}

function isBankFieldRequired(
  profile: CountryProfile | null,
  key: (typeof bankProfileKeys)[number],
): boolean {
  return profile?.[key]?.requirement === "Required";
}

/** Visible/required fields follow the country's profile endpoint, as in org settings. */
export function BankDetailsFields({
  value,
  onChange,
}: {
  value: BankDetailsValue;
  onChange: (next: BankDetailsValue) => void;
}) {
  const api = useDolfinClient();
  const set = (patch: Partial<BankDetailsValue>) => onChange({ ...value, ...patch });

  const [countries, setCountries] = useState<CountrySummary[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyResponse[]>([]);
  const [profile, setProfile] = useState<CountryProfile | null>(null);
  const [countryOpen, setCountryOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const countriesFetched = useRef(false);
  const currenciesFetched = useRef(false);

  useEffect(() => {
    if (countriesFetched.current) return;
    countriesFetched.current = true;
    api.GET("/v1/bank-details/countries").then(({ data }) => {
      if (data) setCountries(data);
    });
  }, [api]);

  useEffect(() => {
    if (currenciesFetched.current) return;
    currenciesFetched.current = true;
    api.GET("/v1/currencies/available").then(({ data }) => {
      if (data) setCurrencies(data);
    });
  }, [api]);

  useEffect(() => {
    if (!value.country) {
      setProfile(null);
      return;
    }
    let cancelled = false;
    api
      .GET("/v1/bank-details/countries/{code}/profile", {
        params: { path: { code: value.country } },
      })
      .then(({ data }) => {
        if (!cancelled) setProfile(data ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [api, value.country]);

  // Pre-populate currency from the country profile when none is set yet.
  useEffect(() => {
    if (profile && !value.currency && profile.defaultCurrencies.length > 0) {
      set({ currency: profile.defaultCurrencies[0]! });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const selectedCountry = countries.find((c) => c.countryCode === value.country);
  const selectedCurrency = currencies.find((c) => c.code === value.currency);

  return (
    <div className="space-y-3">
      <Label className="text-xs font-bold tracking-wider text-muted-foreground uppercase">
        Bank details
      </Label>

      <Input
        placeholder="Account holder name"
        value={value.accountHolderName}
        onChange={(e) => set({ accountHolderName: e.target.value })}
      />

      <div className="grid grid-cols-2 gap-4">
        {/* Country drives which fields below are shown/required. */}
        <Popover open={countryOpen} onOpenChange={setCountryOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={countryOpen}
              className="w-full justify-between font-normal"
            >
              <span className={cn("truncate", !value.country && "text-muted-foreground")}>
                {value.country
                  ? selectedCountry
                    ? `${selectedCountry.countryCode} - ${selectedCountry.countryName}`
                    : value.country
                  : "Bank country..."}
              </span>
              <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
            <Command>
              <CommandInput placeholder="Search countries..." />
              <CommandList>
                <CommandEmpty>No country found.</CommandEmpty>
                <CommandGroup>
                  {countries.map((c) => (
                    <CommandItem
                      key={c.countryCode}
                      value={`${c.countryCode} ${c.countryName}`}
                      onSelect={() => {
                        set({ country: c.countryCode });
                        setCountryOpen(false);
                      }}
                    >
                      <CheckIcon
                        className={cn(
                          "mr-2 size-4",
                          value.country === c.countryCode ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="mr-2 font-mono text-xs text-muted-foreground">
                        {c.countryCode}
                      </span>
                      {c.countryName}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <Popover open={currencyOpen} onOpenChange={setCurrencyOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={currencyOpen}
              className="w-full justify-between font-normal"
            >
              <span className={cn("truncate", !value.currency && "text-muted-foreground")}>
                {value.currency
                  ? selectedCurrency
                    ? `${selectedCurrency.code} - ${selectedCurrency.name}`
                    : value.currency
                  : profile?.defaultCurrencies[0]
                    ? `Currency (e.g. ${profile.defaultCurrencies[0]})`
                    : "Currency..."}
              </span>
              <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
            <Command>
              <CommandInput placeholder="Search currencies..." />
              <CommandList>
                <CommandEmpty>No currency found.</CommandEmpty>
                <CommandGroup>
                  {currencies.map((c) => (
                    <CommandItem
                      key={c.code}
                      value={`${c.code} ${c.name}`}
                      onSelect={() => {
                        set({ currency: c.code });
                        setCurrencyOpen(false);
                      }}
                    >
                      <CheckIcon
                        className={cn(
                          "mr-2 size-4",
                          value.currency === c.code ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="mr-2 font-mono text-xs text-muted-foreground">
                        {c.code}
                      </span>
                      {c.name}
                      <span className="ml-auto text-muted-foreground">{c.symbol}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {(isBankFieldVisible(profile, "sortCode") ||
        isBankFieldVisible(profile, "routingNumber")) && (
        <div className="grid grid-cols-2 gap-4">
          {isBankFieldVisible(profile, "sortCode") && (
            <Input
              placeholder={profile?.sortCode?.placeholder ?? "Sort code"}
              value={value.sortCode}
              onChange={(e) => set({ sortCode: e.target.value })}
            />
          )}
          {isBankFieldVisible(profile, "routingNumber") && (
            <Input
              placeholder={profile?.routingNumber?.placeholder ?? "Routing number"}
              value={value.routingNumber}
              onChange={(e) => set({ routingNumber: e.target.value })}
            />
          )}
        </div>
      )}

      {isBankFieldVisible(profile, "accountNumber") && (
        <Input
          placeholder={
            profile?.accountNumber?.example ??
            profile?.accountNumber?.label ??
            "Account number"
          }
          value={value.accountNumber}
          onChange={(e) => set({ accountNumber: e.target.value })}
        />
      )}

      {(isBankFieldVisible(profile, "iban") ||
        isBankFieldVisible(profile, "bicSwift")) && (
        <div className="grid grid-cols-2 gap-4">
          {isBankFieldVisible(profile, "iban") && (
            <Input
              placeholder={profile?.iban?.example ?? "IBAN"}
              value={value.iban}
              onChange={(e) => set({ iban: e.target.value.toUpperCase() })}
            />
          )}
          {isBankFieldVisible(profile, "bicSwift") && (
            <Input
              placeholder={profile?.bicSwift?.placeholder ?? "BIC / SWIFT"}
              value={value.bicSwift}
              onChange={(e) => set({ bicSwift: e.target.value.toUpperCase() })}
            />
          )}
        </div>
      )}
    </div>
  );
}

export function BankDetailsDisplay({
  bankDetails,
}: {
  bankDetails: BankDetails | null | undefined;
}) {
  if (!hasBankDetails(bankDetails)) return null;
  return (
    <div className="space-y-2.5">
      <DetailRow label="Account holder" value={bankDetails?.accountHolderName} />
      <DetailRow label="Account number" value={bankDetails?.accountNumber} />
      <DetailRow label="Sort code" value={bankDetails?.sortCode} />
      <DetailRow label="Routing number" value={bankDetails?.routingNumber} />
      <DetailRow label="IBAN" value={bankDetails?.iban} mono />
      <DetailRow label="BIC / SWIFT" value={bankDetails?.bicSwift} mono />
      <DetailRow label="Country" value={bankDetails?.country} />
      <DetailRow label="Currency" value={bankDetails?.currency} />
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("text-right font-medium", mono && "font-mono text-xs")}>
        {value}
      </span>
    </div>
  );
}
